/**
 * Session status pings — a short tone when a session's dot changes to
 * green (work finished, ready for the next instruction) or red (terminated
 * / error / rate limit). Pure Web Audio, no asset files, works in Electron.
 *
 * Browsers gate audio behind a user gesture; we lazily create the context
 * and resume it. Since the user clicks around the Sessions rail, the context
 * is unlocked by the time a run finishes.
 */

let ctx: AudioContext | null = null;

function audio(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (!ctx) {
    try {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      ctx = new AC();
    } catch {
      return null;
    }
  }
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  return ctx;
}

/**
 * done  → rising two-note "ding" (a session finished, dot went green)
 * error → lower falling two-note "buzz" (a session terminated/errored, dot went red)
 */
export function playPing(kind: 'done' | 'error' = 'done'): void {
  const c = audio();
  if (!c) return;
  const now = c.currentTime;
  const notes = kind === 'done' ? [660, 988] : [392, 294]; // E5→B5 rise · G4→D4 fall
  notes.forEach((freq, i) => {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = kind === 'done' ? 'sine' : 'triangle';
    osc.frequency.value = freq;
    const t0 = now + i * 0.13;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(kind === 'done' ? 0.16 : 0.2, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.24);
    osc.connect(gain);
    gain.connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + 0.26);
  });
}
